import React, { useMemo } from "react";
import PropTypes from "prop-types";
import SortableTable from "../Tables/SortableTable";
import CustomPagination from "../Tables/CustomPagination";
import InteractiveTableCell from "../Tables/InteractiveTableCell";
import { mediumDateFormat } from "../../utilities/utilities";
import {
	buildAutoRows,
	computeCompareDisabledTexts,
	buildVersionCommentMenuItem,
	buildPreviewMenuItem,
	buildCompareMenuItems,
} from "./versionUtils";
import { useVersionTableState } from "./useVersionTableState";

export default function AutoVersioningTable({
	tableUniqueID,
	documentList,
	documentID,
	documentDetails,
	documentLifecycleStatusObject,
	userRights,
	ableToModify,
	cognitoSub,
	deleteVersion,
	openGlobalDialog,
	updateVersionComment,
	getSignedUrlForDocumentAndDownload,
	openDocumentInLauncher,
	onPreview,
	addDocumentToCompare,
	firstDocIdInDocumentCompare,
	firstVersionIdInDocumentCompare,
	docCompareType,
	isValidLiteraFileType,
	isValidInlineFileType,
	isLiteraCompareVisible,
	isLiteraCompareEnabled,
	isInlineCompareEnabled,
	launcherInstalled,
}) {
	const { maxVersionNumber, deleteDisabledInfo } = useVersionTableState({
		documentList,
		userRights,
		documentLifecycleStatusObject,
	});

	const visibleRows = useMemo(
		() => buildAutoRows(documentList),
		[documentList]
	);

	const automaticColumns = useMemo(
		() => [
			{
				Header: "Ver",
				accessor: "verNum",
				Cell: (row) => {
					const isLatest = row.original.verNum === maxVersionNumber;
					const { literaDisabledText, inlineDisabledText } =
						computeCompareDisabledTexts({
							versionID: row.original.versionID,
							isLatest,
							documentID,
							firstDocIdInDocumentCompare,
							firstVersionIdInDocumentCompare,
							docCompareType,
							isValidLiteraFileType,
							isValidInlineFileType,
							isLiteraCompareVisible,
							isLiteraCompareEnabled,
							isInlineCompareEnabled,
							launcherInstalled,
							documentLifecycleStatusObject,
						});
					return (
						<InteractiveTableCell
							menuItems={[
								isLatest &&
								ableToModify &&
								!documentLifecycleStatusObject.disabled
									? {
											text: "Open",
											onSelect: () =>
												openDocumentInLauncher(
													documentID,
													cognitoSub
												),
										}
									: {
											text: "Download a Copy",
											onSelect: () =>
												getSignedUrlForDocumentAndDownload(
													documentID,
													row.original.versionID
												),
										},
								buildVersionCommentMenuItem({
									menuLabel: `v${row.original.verNum} Version Comment`,
									dialogLabel: `v${row.original.verNum}`,
									currentComment: row.original.comment,
									isMajorVersion: true,
									disabled:
										documentLifecycleStatusObject.disabled,
									disabledHoverText:
										documentLifecycleStatusObject.disabledText,
									openGlobalDialog,
									onSubmit: (value) =>
										updateVersionComment(
											documentID,
											row.original.verNum,
											row.original.versionID,
											value
										),
								}),
								buildPreviewMenuItem({
									versionNumber: row.original.verNum,
									versionID: row.original.versionID,
									onPreview,
								}),
								...buildCompareMenuItems({
									versionID: row.original.versionID,
									documentID,
									documentDetails,
									addDocumentToCompare,
									isValidLiteraFileType,
									isValidInlineFileType,
									documentLifecycleStatusObject,
									literaDisabledText,
									inlineDisabledText,
								}),
								{
									text: "Delete Version",
									disabled:
										documentList.length < 2 ||
										deleteDisabledInfo.disabled,
									disabledHoverText:
										documentList.length < 2
											? "All Versions of a Document cannot be deleted"
											: deleteDisabledInfo.disabledHoverText,
									onSelect: () =>
										deleteVersion({
											key: documentDetails.prefix,
											documentID,
											versionID: row.original.versionID,
											versionNumber: row.original.verNum,
											itemNoun: "Version",
										}),
								},
							]}
						>
							<span className="tagPill">{row.original.ver}</span>
						</InteractiveTableCell>
					);
				},
				width: 110,
			},
			{
				Header: "Created",
				accessor: "createdAt",
				Cell: (row) => mediumDateFormat(row.value),
				width: 150,
			},
			{
				Header: "By",
				accessor: "createdBy",
				width: 100,
			},
			{
				Header: "Comment",
				accessor: "comment",
				Cell: (row) => (
					<div
						style={{ overflow: "hidden", textOverflow: "ellipsis" }}
						title={row.value}
					>
						{row.value}
					</div>
				),
			},
		],
		[
			maxVersionNumber,
			documentID,
			documentDetails,
			documentList,
			deleteDisabledInfo,
			deleteVersion,
			openGlobalDialog,
			updateVersionComment,
			getSignedUrlForDocumentAndDownload,
			openDocumentInLauncher,
			ableToModify,
			cognitoSub,
			onPreview,
			addDocumentToCompare,
			firstDocIdInDocumentCompare,
			firstVersionIdInDocumentCompare,
			docCompareType,
			isValidLiteraFileType,
			isValidInlineFileType,
			isLiteraCompareVisible,
			isLiteraCompareEnabled,
			isInlineCompareEnabled,
			launcherInstalled,
			documentLifecycleStatusObject,
		]
	);

	return (
		<div style={{ marginBottom: 24 }}>
			<div style={{ height: 470 }}>
				<SortableTable
					className="fullWidth"
					tableUniqueID={tableUniqueID}
					data={visibleRows}
					columns={automaticColumns}
					defaultPageSize={10}
					defaultSorted={[{ id: "verNum", desc: true }]}
					uncontrolledPagination
					PaginationComponent={CustomPagination}
					noResultsText="No versions"
				/>
			</div>
		</div>
	);
}

AutoVersioningTable.propTypes = {
	tableUniqueID: PropTypes.string.isRequired,
	documentList: PropTypes.array.isRequired,
	documentID: PropTypes.number.isRequired,
	documentDetails: PropTypes.object,
	documentLifecycleStatusObject: PropTypes.object,
	userRights: PropTypes.object,
	ableToModify: PropTypes.bool,
	cognitoSub: PropTypes.string,
	deleteVersion: PropTypes.func,
	getSignedUrlForDocumentAndDownload: PropTypes.func,
	openDocumentInLauncher: PropTypes.func,
	openGlobalDialog: PropTypes.func.isRequired,
	updateVersionComment: PropTypes.func.isRequired,
	onPreview: PropTypes.func,
	addDocumentToCompare: PropTypes.func,
	firstDocIdInDocumentCompare: PropTypes.number,
	firstVersionIdInDocumentCompare: PropTypes.string,
	docCompareType: PropTypes.string,
	isValidLiteraFileType: PropTypes.bool,
	isValidInlineFileType: PropTypes.bool,
	isLiteraCompareVisible: PropTypes.bool,
	isLiteraCompareEnabled: PropTypes.bool,
	isInlineCompareEnabled: PropTypes.bool,
	launcherInstalled: PropTypes.bool,
};
